// React imports removed - using custom hook instead

import { useAxios } from '@/hooks/useAxios';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

import { useFilterValues } from './useFilterValues';

interface DropdownWithLazyLoadProps {
  filterDef: SimpleFilterDefinition;
  currentValue: string | string[];
  onChange: (newValue: string) => void;
  disabled?: boolean;
}

/**
 * Dropdown that lazy-loads values when opened
 * Fetches values via fieldDef.getAvailableValues() only when user interacts
 */
export const DropdownWithLazyLoad = ({
  filterDef,
  currentValue,
  onChange,
  disabled = false,
}: DropdownWithLazyLoadProps) => {
  // Get authenticated axios instance for API calls
  const axios = useAxios();
  const { options, isLoading, error } = useFilterValues(filterDef, axios);

  // Ensure currentValue is always in options for proper select display
  // This handles the case where options haven't loaded yet but we need to display the value
  const displayOptions = (() => {
    if (!currentValue) return options;

    // If currentValue is array, we need to join it for display
    const valueString = String(currentValue);

    // If currentValue is already in options, just return options
    if (options.includes(valueString)) return options;

    // Otherwise, prepend currentValue to ensure select has matching option
    return [valueString, ...options];
  })();

  return (
    <select
      disabled={disabled || isLoading}
      value={String(currentValue)}
      onChange={e => onChange(e.target.value)}
      className={`flex-[3.5] rounded border px-3 py-1.5 text-sm ${
        disabled || isLoading
          ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
          : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary'
      }`}
      aria-label="Filter value"
    >
      {!currentValue && (
        <option value="">
          {isLoading ? 'Loading...' : error ? error : 'Select value...'}
        </option>
      )}
      {displayOptions.map(val => (
        <option key={val} value={val}>
          {val}
        </option>
      ))}
    </select>
  );
};
